import { atom, AtomEffect } from 'recoil';

type SpeciesOption = {
  label: string;
  children: Array<{
    label: string;
  }>;
};

export type SpeciesFilterMode = 'all' | 'custom';
const localStorageEffect = <T>(key: string): AtomEffect<T> => ({ setSelf, onSet }) => {
  if (typeof window === 'undefined') {
    return;
  }

  const savedValue = window.localStorage.getItem(key);
  if (savedValue != null) {
    try {
      setSelf(JSON.parse(savedValue));
    } catch {
      window.localStorage.removeItem(key);
    }
  }

  onSet((newValue, _, isReset) => {
    if (isReset) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(newValue));
    }
  });
};

const oneMonthBack = new Date();
oneMonthBack.setMonth(oneMonthBack.getMonth() - 1);

const dateRangeEffect: AtomEffect<{ startDate: Date; endDate: Date }> = ({ setSelf, onSet }) => {
  if (typeof window === 'undefined') {
    return;
  }

  const savedValue = window.localStorage.getItem('dateRange');
  if (savedValue != null) {
    try {
      const parsed = JSON.parse(savedValue);
      setSelf({
        startDate: new Date(parsed.startDate),
        endDate: new Date(parsed.endDate),
      });
    } catch {
      window.localStorage.removeItem('dateRange');
    }
  }

  onSet((newValue, _, isReset) => {
    if (isReset) {
      window.localStorage.removeItem('dateRange');
    } else {
      window.localStorage.setItem('dateRange', JSON.stringify({
        startDate: newValue.startDate.toISOString(),
        endDate: newValue.endDate.toISOString(),
      }));
    }
  });
};

export const dateRangeState = atom({
  key: 'dateRangeState',
  default: {
    startDate: oneMonthBack,
    endDate: new Date(),
  },
  effects_UNSTABLE: [dateRangeEffect],
});

export const dateValueState = atom({
  key: 'dateValueState',
  default: 1,
  effects_UNSTABLE: [localStorageEffect<number>('dateValue')],
});

export const speciesPercentageDateRangeState = atom({
  key: 'speciesPercentageDateRangeState',
  default: [],
});

const siteEffect: AtomEffect<string> = ({ setSelf, onSet }) => {
  if (typeof window === 'undefined') {
    return;
  }

  const savedValue = window.localStorage.getItem('site');
  if (savedValue != null) {
    try {
      const parsed = JSON.parse(savedValue);
      setSelf(parsed);
    } catch {
      window.localStorage.removeItem('site');
    }
  }

  onSet((newValue, _, isReset) => {
    if (isReset) {
      window.localStorage.removeItem('site');
    } else {
      window.localStorage.setItem('site', JSON.stringify(newValue));
    }
  });
};

export const siteState = atom({
  key: 'siteState',
  default: '',
  effects_UNSTABLE: [siteEffect],
});

export const cameraState = atom<number[]>({
  key: 'cameraState',
  default: [],
  effects_UNSTABLE: [localStorageEffect<number[]>('camera')],
});

export const selectedOrdersState = atom<string[]>({
  key: 'selectedOrdersState',
  default: [],
  effects_UNSTABLE: [localStorageEffect<string[]>('selectedOrders')],
});

export const selectedSpeciesState = atom<string[]>({
  key: 'selectedSpeciesState',
  default: [],
  effects_UNSTABLE: [localStorageEffect<string[]>('selectedSpecies')],
});

export const dateRangeAdjustedState = atom<boolean>({
  key: 'dateRangeAdjustedState',
  default: false,
});

export const speciesListState = atom<SpeciesOption[] | null>({
  key: 'speciesListState',
  default: null,
});

export const speciesFilterModeState = atom<SpeciesFilterMode>({
  key: 'speciesFilterModeState',
  default: 'all',
  effects_UNSTABLE: [localStorageEffect<SpeciesFilterMode>('speciesFilterMode')],
});
